/**
 * Pure access predicates over a tenant's subscription lifecycle status.
 *
 * Single source of truth (repo rule #9) for "what a tenant in status X may do".
 * Reused by the Cloud Function money-path gates (`bookTicket`, `addOutlet`),
 * the customer-pwa "temporarily closed" screen, and the dashboard
 * "suspended — pay to reactivate" lock. NO feature code should hand-roll a
 * `status === 'suspended'` check — call these instead so the policy lives in
 * exactly one place.
 *
 * The status lifecycle itself (`trial → active ⇄ past_due → suspended →
 * archived`) is decided by `decideTenantLifecycle` in ./tenant-lifecycle.ts;
 * this module only answers the access question for a given resting status.
 */

import type { TenantStatus } from '../schemas/tenant.js';

/**
 * True when the tenant may take NEW bookings / open a new outlet — i.e. the
 * money paths are open. False ONLY for `suspended` and `archived`:
 *
 *   - `trial` / `active`  — normal operation.
 *   - `past_due`          — a dunning state, NOT a hard stop. The past-due
 *                            banner is a separate concern (roadmap #9); a
 *                            past-due tenant still transacts so an operator
 *                            mid-cycle isn't cut off the instant a due date
 *                            slips. Suspension is the heavier hammer.
 *   - `suspended`         — new bookings + new outlets blocked; in-progress
 *                            work is intentionally NOT gated here (staff may
 *                            finish washes already in the queue).
 *   - `archived`          — terminal; blocked.
 */
export function tenantCanTakeBookings(status: TenantStatus): boolean {
  return status !== 'suspended' && status !== 'archived';
}

/**
 * True only in the `suspended` state — drives the pay-to-reactivate lock
 * (dashboard) and the temporarily-closed screen (customer-pwa). Kept distinct
 * from `tenantCanTakeBookings` because `archived` shows a different (terminal)
 * message, not a "pay to come back" prompt.
 */
export function isTenantSuspended(status: TenantStatus): boolean {
  return status === 'suspended';
}
